import React, { useState } from 'react';
import { Box, Typography, List, ListItem, ListItemIcon, ListItemText, Badge, Divider, Avatar, Collapse, TextField, InputAdornment, IconButton, Chip } from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import LogoutIcon from '@mui/icons-material/Logout';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import HomeIcon from '@mui/icons-material/Home';
import PsychologyIcon from '@mui/icons-material/Psychology';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import TokenIcon from '@mui/icons-material/Token';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import DescriptionIcon from '@mui/icons-material/Description';
import FeedbackIcon from '@mui/icons-material/Feedback';
import SchoolIcon from '@mui/icons-material/School';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddIcon from '@mui/icons-material/Add';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CategoryIcon from '@mui/icons-material/Category';
import SearchIcon from '@mui/icons-material/Search';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SettingsIcon from '@mui/icons-material/Settings';
import BarChartIcon from '@mui/icons-material/BarChart';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import ScienceIcon from '@mui/icons-material/Science';
import CodeIcon from '@mui/icons-material/Code';
import { useNavigate } from 'react-router-dom';

// Embedded theme - no external imports needed
const theme = {
    colors: {
        bg: { primary: '#191b20', secondary: '#292b2f', tertiary: '#333539', quaternary: '#3f4147' },
        text: { primary: 'rgba(255,255,255,1)', secondary: 'rgba(255,255,255,0.9)', tertiary: 'rgba(255,255,255,0.85)', quaternary: 'rgba(255,255,255,0.75)', placeholder: 'rgba(255,255,255,0.6)' },
        accent: { primary: '#7c3aed', secondary: '#5b21b6', tertiary: '#a78bfa' },
        border: { primary: 'rgba(255,255,255,0.1)', secondary: 'rgba(255,255,255,0.05)', tertiary: 'rgba(255,255,255,0.15)' },
        status: { success: '#22c55e', error: '#ef4444', warning: '#f59e0b' }
    },
    gradients: { primaryAccent: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)' },
    borderRadius: { sm: '8px', md: '12px' },
    shadows: { large: '0 12px 24px rgba(0,0,0,0.4)' },
    transitions: { hover: '200ms cubic-bezier(0.4,0,0.2,1)' },
    zIndex: { sticky: 200 }
};

const AdminSidebar = ({ activeTab, onTabChange, onLogout, onCreateUser, onCreateStrategy }) => {
    const navigate = useNavigate();
    const [expandedMenus, setExpandedMenus] = useState([]); // Collapsed by default

    const handleMainDashboard = () => {
        navigate('/dashboard');
    };

    const handleMenuToggle = (menuId) => {
        setExpandedMenus(prev =>
            prev.includes(menuId)
                ? prev.filter(id => id !== menuId)
                : [...prev, menuId]
        );
    };

    const handleSubmenuClick = (parentId, submenuId) => {
        // Handle special modal actions
        if (submenuId === 'create-user' && onCreateUser) {
            onCreateUser();
            onTabChange('user-management'); // Navigate to user management page
            return;
        }
        if (submenuId === 'create-strategy' && onCreateStrategy) {
            onCreateStrategy();
            onTabChange('strategy-management'); // Navigate to strategy management page
            return;
        }
        // Navigate to Pre-built Templates page
        if (submenuId === 'templates') {
            navigate('/adminPanel/templates');
            return;
        }

        // Default behavior: navigate to parent menu
        if (!expandedMenus.includes(parentId)) {
            setExpandedMenus(prev => [...prev, parentId]);
        }
        onTabChange(parentId);
    };

    // Organized menu structure with sections
    const menuSections = [
        {
            title: 'General',
            items: [
                {
                    id: 'dashboard',
                    label: 'Dashboard',
                    icon: <DashboardIcon />,
                    badge: null,
                    submenu: null
                },
                {
                    id: 'user-management',
                    label: 'User Management',
                    icon: <PeopleIcon />,
                    badge: null,
                    submenu: [
                        { id: 'create-user', label: 'Create User', icon: <PersonAddIcon /> }
                    ]
                },
                {
                    id: 'strategy-management',
                    label: 'Strategy Management',
                    icon: <PsychologyIcon />,
                    badge: null,
                    submenu: [
                        { id: 'templates', label: 'Pre-built Templates', icon: <CategoryIcon /> },
                        { id: 'create-strategy', label: 'Create Strategy', icon: <AddIcon /> }
                    ]
                }
            ]
        },
        {
            title: 'Q AI Management',
            items: [
                {
                    id: 'qai-users',
                    label: 'Q AI Users',
                    icon: <SmartToyIcon />,
                    badge: null,
                    submenu: [
                        { id: 'credit-tokens', label: 'Credit Tokens', icon: <TokenIcon /> }
                    ]
                },
                {
                    id: 'qai-transactions',
                    label: 'Token Transactions',
                    icon: <ReceiptLongIcon />,
                    badge: null,
                    submenu: null
                },
                {
                    id: 'qai-prompts',
                    label: 'Prompt Management',
                    icon: <DescriptionIcon />,
                    badge: null,
                    submenu: null
                },
                {
                    id: 'qai-feedback',
                    label: 'Feedback & Training',
                    icon: <FeedbackIcon />,
                    badge: null,
                    submenu: null
                },
                {
                    id: 'qai-training',
                    label: 'Training Data',
                    icon: <SchoolIcon />,
                    badge: null,
                    submenu: null
                },
                {
                    id: 'qai-settings',
                    label: 'QAI Settings',
                    icon: <SettingsIcon />,
                    badge: null,
                    submenu: null
                },
                {
                    id: 'qai-eval',
                    label: 'Eval Pipeline',
                    icon: <ScienceIcon />,
                    badge: null,
                    submenu: null
                },
                {
                    id: 'qai-ea-analysis',
                    label: 'EA Code Guide',
                    icon: <CodeIcon />,
                    badge: null,
                    submenu: null
                }
            ]
        }
    ];

    return (
        <Box sx={{
            width: { xs: '100%', sm: 280 },
            height: '100vh',
            background: theme.colors.bg.primary,
            color: theme.colors.text.primary,
            display: 'flex',
            flexDirection: 'column',
            position: { xs: 'relative', sm: 'fixed' },
            left: 0,
            top: 0,
            zIndex: theme.zIndex.sticky,
            boxShadow: theme.shadows.large,
            borderRight: `1px solid ${theme.colors.border.secondary}`
        }}>
            {/* User Profile Section - Top */}
            <Box sx={{
                p: 2.5,
                borderBottom: `1px solid ${theme.colors.border.secondary}`
            }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    mb: 2
                }}>
                    <Avatar sx={{
                        width: 48,
                        height: 48,
                        background: theme.gradients.primaryAccent,
                        color: theme.colors.text.primary,
                        fontWeight: 700,
                        fontSize: '1.1rem',
                        border: `2px solid ${theme.colors.border.primary}`
                    }}>
                        JD
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                        <Typography sx={{
                            color: theme.colors.text.primary,
                            fontWeight: 700,
                            fontSize: '1.2rem',
                            lineHeight: 1.3
                        }}>
                            John Doe
                        </Typography>
                        <Typography sx={{
                            color: theme.colors.text.quaternary,
                            fontSize: '1rem',
                            fontWeight: 500
                        }}>
                            Super Admin
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Quick Access - Trading Dashboard */}
            <Box sx={{ px: 2.5, pt: 2, pb: 2 }}>
                <Box
                    onClick={handleMainDashboard}
                    sx={{
                        p: 1.5,
                        borderRadius: theme.borderRadius.md,
                        cursor: 'pointer',
                        background: `${theme.colors.bg.secondary}`,
                        border: `1px solid ${theme.colors.border.primary}`,
                        transition: theme.transitions.hover,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        '&:hover': {
                            background: 'rgba(124, 58, 237, 0.15)',
                            border: `1px solid ${theme.colors.accent.primary}`,
                            transform: 'translateX(4px)'
                        }
                    }}
                >
                    <Box sx={{
                        width: 36,
                        height: 36,
                        background: theme.colors.status.success,
                        borderRadius: theme.borderRadius.sm,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <HomeIcon sx={{ fontSize: 20, color: theme.colors.text.primary }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography sx={{
                            color: theme.colors.text.primary,
                            fontWeight: 600,
                            fontSize: '1.1rem',
                            lineHeight: 1.3
                        }}>
                            Trading Dashboard
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <Divider sx={{ borderColor: theme.colors.border.secondary, mx: 2 }} />

            {/* Navigation with Sections */}
            <Box sx={{ py: 2, px: 2, flex: 1, overflowY: 'auto' }}>
                {menuSections.map((section, sectionIndex) => (
                    <Box key={section.title} sx={{ mb: sectionIndex < menuSections.length - 1 ? 3 : 0 }}>
                        {/* Section Header */}
                        <Typography sx={{
                            color: theme.colors.text.placeholder,
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            letterSpacing: '0.12em',
                            textTransform: 'uppercase',
                            mb: 1.5,
                            px: 1
                        }}>
                            {section.title}
                        </Typography>

                        {/* Menu Items in Section */}
                        <List sx={{ p: 0 }}>
                            {section.items.map((item) => (
                                <React.Fragment key={item.id}>
                                    {/* Parent Menu Item */}
                                    <ListItem
                                        onClick={() => {
                                            // Always navigate to the main page
                                            onTabChange(item.id);
                                            // Also toggle submenu if exists
                                            if (item.submenu) {
                                                handleMenuToggle(item.id);
                                            }
                                        }}
                                        sx={{
                                            mb: item.submenu && expandedMenus.includes(item.id) ? 0.5 : 1,
                                            borderRadius: theme.borderRadius.md,
                                            cursor: 'pointer',
                                            transition: theme.transitions.hover,
                                            background: 'transparent',
                                            border: activeTab === item.id
                                                ? '2px solid #3b82f6'
                                                : '1px solid transparent',
                                            py: 1.25,
                                            px: 1.5,
                                            '&:hover': {
                                                background: 'rgba(124, 58, 237, 0.15)',
                                                transform: 'translateX(4px)',
                                                border: activeTab === item.id
                                                    ? '2px solid #3b82f6'
                                                    : `1px solid ${theme.colors.accent.primary}`
                                            }
                                        }}
                                    >
                                        <ListItemIcon sx={{
                                            minWidth: 40,
                                            color: activeTab === item.id
                                                ? theme.colors.accent.tertiary
                                                : theme.colors.text.tertiary
                                        }}>
                                            {item.icon}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={item.label}
                                            primaryTypographyProps={{
                                                sx: {
                                                    fontWeight: activeTab === item.id ? 600 : 500,
                                                    fontSize: '1.15rem',
                                                    color: activeTab === item.id
                                                        ? theme.colors.accent.tertiary
                                                        : theme.colors.text.secondary
                                                }
                                            }}
                                        />
                                        {item.badge && (
                                            <Chip
                                                label={item.badge.text}
                                                size="small"
                                                sx={{
                                                    height: 22,
                                                    background: item.badge.color,
                                                    color: theme.colors.text.primary,
                                                    fontWeight: 700,
                                                    fontSize: '0.65rem',
                                                    letterSpacing: '0.05em',
                                                    mr: 1,
                                                    '& .MuiChip-label': {
                                                        px: 1
                                                    }
                                                }}
                                            />
                                        )}
                                        {item.submenu && (
                                            <ListItemIcon sx={{
                                                minWidth: 'auto',
                                                color: theme.colors.text.quaternary,
                                                ml: 0.5
                                            }}>
                                                {expandedMenus.includes(item.id) ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                            </ListItemIcon>
                                        )}
                                    </ListItem>

                                    {/* Submenu Items */}
                                    {item.submenu && (
                                        <Collapse in={expandedMenus.includes(item.id)} timeout="auto" unmountOnExit>
                                            <List sx={{ pl: 2, pr: 0, pt: 0, pb: 1 }}>
                                                {item.submenu.map((subitem) => (
                                                    <ListItem
                                                        key={subitem.id}
                                                        onClick={() => handleSubmenuClick(item.id, subitem.id)}
                                                        sx={{
                                                            mb: 0.5,
                                                            borderRadius: theme.borderRadius.sm,
                                                            cursor: 'pointer',
                                                            transition: theme.transitions.hover,
                                                            background: 'transparent',
                                                            border: '1px solid transparent',
                                                            pl: 1.5,
                                                            py: 0.75,
                                                            '&:hover': {
                                                                background: 'rgba(124, 58, 237, 0.15)',
                                                                border: `1px solid ${theme.colors.accent.primary}`,
                                                                transform: 'translateX(4px)'
                                                            }
                                                        }}
                                                    >
                                                        <ListItemIcon sx={{
                                                            minWidth: 32,
                                                            color: theme.colors.text.quaternary
                                                        }}>
                                                            {subitem.icon}
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={subitem.label}
                                                            primaryTypographyProps={{
                                                                sx: {
                                                                    fontWeight: 500,
                                                                    fontSize: '1rem',
                                                                    color: theme.colors.text.tertiary
                                                                }
                                                            }}
                                                        />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </Collapse>
                                    )}
                                </React.Fragment>
                            ))}
                        </List>
                    </Box>
                ))}
            </Box>

            <Divider sx={{ borderColor: theme.colors.border.secondary }} />

            {/* Bottom Navigation Icons */}
            <Box sx={{ p: 2 }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-around',
                    background: theme.colors.bg.secondary,
                    borderRadius: theme.borderRadius.md,
                    border: `1px solid ${theme.colors.border.primary}`,
                    p: 1.5
                }}>
                    <IconButton
                        sx={{
                            width: 42,
                            height: 42,
                            color: theme.colors.text.tertiary,
                            transition: theme.transitions.hover,
                            '&:hover': {
                                background: theme.colors.bg.tertiary,
                                color: theme.colors.accent.tertiary
                            }
                        }}
                    >
                        <SettingsIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        sx={{
                            width: 42,
                            height: 42,
                            color: theme.colors.text.tertiary,
                            transition: theme.transitions.hover,
                            '&:hover': {
                                background: theme.colors.bg.tertiary,
                                color: theme.colors.accent.tertiary
                            }
                        }}
                    >
                        <BarChartIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        onClick={onLogout}
                        sx={{
                            width: 42,
                            height: 42,
                            color: theme.colors.status.error,
                            transition: theme.transitions.hover,
                            '&:hover': {
                                background: `${theme.colors.status.error}20`,
                                color: theme.colors.status.error
                            }
                        }}
                    >
                        <PowerSettingsNewIcon fontSize="small" />
                    </IconButton>
                </Box>
            </Box>
        </Box>
    );
};

export default AdminSidebar;